import 'core-js/stable';
import 'regenerator-runtime/runtime';

import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom';
import { ThemeProvider } from 'styled-components';
import { QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';

import store from '../lib/redux/store';
import { ActionCableContext, cable } from '../lib/actionCableContext';
import airboTheme from '../themes/airbo';
import ErrorBoundary from '../lib/ErrorBoundary';
import GlobalStyle from '../shared/GlobalStyle';
import NotificationList from '../shared/Notification';
import queryClient from '../lib/queryClient';

const NavigationBar = lazy(() => import('../containers/NavigationBar'));
const Reports = lazy(() => import('../containers/reports'));

const ClientsTab = lazy(() => import('../containers/clientAdminClients'));
const VaultTab = lazy(() => import('../containers/vault'));
const ClientsPrizeTab = lazy(() => import('../containers/clientAdminPrizes'));
const UsersTab = lazy(() => import('../containers/clientAdminUsers'));
const AccountSettings = lazy(() => import('../containers/accountSettings'));
const ReactSPA = lazy(() => import('../app'));
const Campaigns = lazy(() => import('../containers/campaigns'));
const CampaignPreview = lazy(() => import('../containers/campaignPreview'));
const Broadcast = lazy(() => import('../containers/broadcastMessages'));
const HrAssistant = lazy(() => import('../containers/hrAssistant'));
const BroadcastMessages = lazy(() => import('../containers/broadcastMessages'));
const Header = lazy(() => import('../containers/Header'));
const BoardSetup = lazy(() => import('../containers/boardSetup'));
const Send = lazy(() => import('../containers/send'));
const EmbedContainer = lazy(() => import('../containers/embed'));

const registry = [
  {
    id: 'NavigationBar',
    component: NavigationBar,
    propsFromNode: node => ({ content: JSON.parse(node.dataset.content) }),
  },
  {
    id: 'Header',
    component: Header,
    propsFromNode: node => ({
      logo: node.dataset.logo,
      shouldConvertGuest: node.dataset.shouldConvertGuest,
      guestUserResetPath: node.dataset.guestUserResetPath,
      home: node.dataset.home,
    }),
  },
  { id: 'Reports', component: Reports },
  {
    id: 'ClientsTab',
    component: ClientsTab,
    propsFromBody: {
      organizationData: JSON.parse(document.body.dataset.currentOrganization),
    },
  },
  {
    id: 'VaultTab',
    component: VaultTab,
    propsFromBody: {
      organizationData: JSON.parse(document.body.dataset.currentOrganization),
    },
  },
  { id: 'ClientsPrizeTab', component: ClientsPrizeTab },
  { id: 'UsersTab', component: UsersTab },
  {
    id: 'BoardSetup',
    component: BoardSetup,
  },
  {
    id: 'Send',
    component: Send,
    propsFromNode: node => ({
      emailPreviewPath: node.dataset.titleDigestEmailPreviewPath,
      smsPreviewPath: node.dataset.titleDigestSmsPreviewPath,
    }),
  },
  {
    id: 'ReactSPA',
    component: ReactSPA,
    propsFromNode: node => ({ data: JSON.parse(node.dataset.ctrl) }),
  },
  {
    id: 'AccountSettings',
    component: AccountSettings,
  },
  {
    id: 'Campaigns',
    component: Campaigns,
  },
  {
    id: 'Broadcast',
    component: Broadcast,
  },
  {
    id: 'HrAssistant',
    component: HrAssistant,
  },
  {
    id: 'BroadcastMessage',
    component: BroadcastMessages,
  },
  {
    id: 'CampaignPreview',
    component: CampaignPreview,
  },
  {
    id: 'EmbedContainer',
    component: EmbedContainer,
  },
];

const empty = () => ({});

const ReactComponents = () =>
  registry
    .map(item => ({
      ...item,
      node: document.getElementById(item.id),
    }))
    .filter(item => Boolean(item.node))
    .map(
      ({
        component: Component,
        node,
        propsFromNode = empty,
        propsFromBody = {},
      }) =>
        ReactDOM.createPortal(
          <Suspense fallback={<div />}>
            <Component {...propsFromNode(node)} {...propsFromBody} />
          </Suspense>,
          node,
        ),
    );

document.addEventListener('DOMContentLoaded', () => {
  ReactDOM.render(
    <ThemeProvider theme={airboTheme}>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <Provider store={store}>
            <ActionCableContext.Provider value={cable}>
              <GlobalStyle />
              <NotificationList />
              <ReactComponents />
            </ActionCableContext.Provider>
          </Provider>
        </ErrorBoundary>
      </QueryClientProvider>
    </ThemeProvider>,
    document.getElementById('react-root'),
  );
});
